<template>
  <DialogContent class="flex flex-col items-center relative">
    <DacrBadge class="size-10" :icon-size="28" />

    <div
      class="font-primary font-semibold text-[44px] leading-[50px] text-gray-800 mb-0.5"
    >
      Pro
    </div>
    <div
      class="font-primary font-semibold text-[24px] leading-[28px] text-gray-800 mb-[17px]"
    >
      {{ headline }}
    </div>

    <Subheadline />

    <div class="self-start">
      <div
        class="font-primary font-semibold text-[14px] leading-[20px] text-gray-800 mb-[19px]"
      >
        What you get
      </div>
      <div class="flex flex-col gap-y-2.5">
        <div class="flex items-center">
          <Icon name="FilledCircleCheck" :size="16" class="mr-3" />
          <div>Copyright protect all your work</div>
        </div>
        <div class="flex items-center">
          <Icon name="FilledCircleCheck" :size="16" class="mr-3" />
          <div class="mr-3">
            Invite and control who can access your copyrights
          </div>
        </div>
        <div class="flex items-center">
          <Icon name="FilledCircleCheck" :size="16" class="mr-3" />
          <div class="mr-3">
            <span
              class="w-fit bg-linear-to-r from-yellow-500 via-[#F84600] to-[#CE489A] bg-clip-text text-transparent font-medium"
            >
              Dacr AI
            </span>
            to discover and fight piracy
          </div>
        </div>
        <div class="flex items-center">
          <Icon name="FilledCircleCheck" :size="16" class="mr-3" />
          <div>Add unlimited co-owners and authors</div>
        </div>
        <div class="flex items-center">
          <Icon name="FilledCircleCheck" :size="16" class="mr-3" />
          <div>License and sell your work + collect royalties</div>
        </div>
        <div class="flex items-center">
          <Icon name="FilledCircleCheck" :size="16" class="mr-3" />
          <div>Manage copyrights on behalf of others</div>
        </div>
      </div>
    </div>

    <div class="w-full h-px bg-gray-800/10 mt-5 mb-[19px]"></div>

    <div class="w-full flex items-end justify-between">
      <div>
        <div class="text-gray-400 leading-[18px] mb-2">Starting at</div>
        <div v-if="plan" class="flex items-end">
          <span
            class="font-primary font-semibold text-[34px] leading-[38px] text-gray-800 mr-1"
          >
            {{ plan?.items[0].prices[0].details?.renderedPrice }}
          </span>
          <span>/month</span>
        </div>
        <Skeleton v-else class="w-[110px] h-[38px]" />
      </div>
      <UiButton primary large :disabled="!plan" @click="handleClick">
        See plans
      </UiButton>
    </div>
  </DialogContent>
</template>

<script setup lang="ts">
import { Icon, UiButton, Skeleton } from '@dacr/ui'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { usePlansQueryQuery } from '@/shared/graphql/legacy/generated'
import { computed, h } from 'vue'
import DacrBadge from '@/shared/components/DacrBadge.vue'
import { useRouter } from 'vue-router'

const props = defineProps<{
  willReachLimit?: boolean
  hasReachedLimit?: boolean
}>()

const { close } = useDialog()

const { result } = usePlansQueryQuery()

const router = useRouter()

const yearlyBillingCycle = computed(() =>
  result.value?.plans?.groupByBillingCycle.find(
    (billingCycle) => billingCycle.billingCycleType === 'month'
  )
)
const plan = computed(() =>
  yearlyBillingCycle.value?.plans.find((plan) => plan.items.length > 1)
)

const headline = computed(() => {
  if (props.hasReachedLimit) {
    return 'Upgrade to add more'
  }

  return 'Do more with your copyrights'
})

const Subheadline = computed(() => {
  if (props.willReachLimit) {
    return h(
      'div',
      {
        class: 'text-[16px] leading-[24px] mb-8 text-center',
      },
      [
        h(
          'div',
          { class: 'font-primary font-medium text-800' },
          'Before you reach the limit of your plan,'
        ),
        h(
          'div',
          { class: 'font-secondary text-gray-500' },
          'upgrade and unlock your potential with Dacr Pro.'
        ),
      ]
    )
  }

  if (props.hasReachedLimit) {
    return h(
      'div',
      {
        class: 'text-[16px] leading-[24px] mb-8 text-center',
      },
      [
        h(
          'div',
          { class: 'font-primary font-medium text-800' },
          'You\u2019ve reached the limit of your current plan.'
        ),
        h(
          'div',
          { class: 'font-secondary text-gray-500' },
          'Upgrade and unlock your potential with Dacr Pro.'
        ),
      ]
    )
  }

  return h(
    'div',
    {
      class:
        'font-secondary text-gray-500 text-[16px] leading-[24px] text-center mb-8',
    },
    [
      h('div', 'No limits on protecting your work.'),
      h('div', 'Unlock your potential with Dacr Pro.'),
    ]
  )
})

const handleClick = () => {
  router.push('/upgrade')
  close()
}
</script>
